import React from "react";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function TextInput({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border rounded"
      />
    </div>
  );
}

function NumberInput({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(toNumber(e.target.value, 0))}
        className="w-full px-3 py-2 border rounded"
      />
    </div>
  );
}

export default function BoardFrameInfillInspector({
  profile,
  onChangeProfile,
  parameters,
  onChangeParameters,
}) {
  const safeParams = parameters || {};

  const safeProfile = profile || {
    type: "framed_panel",
    outerProfile: {
      type: "rect",
      width_expr: "width",
      height_expr: "height",
    },
    frame: {
      enabled: true,
      frameWidth: "frameWidth",
      thickness: "frameThickness",
      materialSlot: "frame",
    },
    infill: {
      enabled: true,
      inset: "frameWidth",
      thickness: "infillThickness",
      materialSlot: "infill",
      type: "glass",
    },
  };

  const frame = safeProfile.frame || {};
  const infill = safeProfile.infill || {};
  const outerProfile = safeProfile.outerProfile || {
    type: "rect",
    width_expr: "width",
    height_expr: "height",
  };

  function patchProfile(patch) {
    onChangeProfile?.({
      ...safeProfile,
      ...patch,
      type: "framed_panel",
    });
  }

  function patchOuterProfile(patch) {
    patchProfile({
      outerProfile: {
        ...outerProfile,
        ...patch,
      },
    });
  }

  function patchFrame(patch) {
    patchProfile({
      frame: {
        ...frame,
        ...patch,
      },
    });
  }

  function patchInfill(patch) {
    patchProfile({
      infill: {
        ...infill,
        ...patch,
      },
    });
  }

  function patchParam(key, value) {
    onChangeParameters?.({
      ...safeParams,
      [key]: value,
    });
  }

  return (
    <div className="p-3 space-y-4">
      <div>
        <h3 className="font-semibold text-lg">Framed Panel</h3>
        <div className="text-xs text-slate-500">
          Parametric frame with optional infill: glass, mirror, board or custom.
        </div>
      </div>

      <div className="border rounded p-3 space-y-3 bg-white">
        <div className="font-semibold text-sm">Base size</div>

        <div className="grid grid-cols-2 gap-2">
          <NumberInput
            label="Width"
            value={safeParams.width ?? 600}
            onChange={(value) => patchParam("width", value)}
          />

          <NumberInput
            label="Height"
            value={safeParams.height ?? 720}
            onChange={(value) => patchParam("height", value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <TextInput
            label="Width expression"
            value={outerProfile.width_expr ?? "width"}
            onChange={(value) => patchOuterProfile({ width_expr: value })}
          />

          <TextInput
            label="Height expression"
            value={outerProfile.height_expr ?? "height"}
            onChange={(value) => patchOuterProfile({ height_expr: value })}
          />
        </div>
      </div>

      <div className="border rounded p-3 space-y-3 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">Frame</div>
            <div className="text-xs text-slate-500">
              Four rails around the infill.
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={frame.enabled !== false}
              onChange={(e) => patchFrame({ enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <NumberInput
            label="Frame width"
            value={safeParams.frameWidth ?? 70}
            onChange={(value) => patchParam("frameWidth", value)}
          />

          <NumberInput
            label="Frame thickness"
            value={safeParams.frameThickness ?? 22}
            onChange={(value) => patchParam("frameThickness", value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <TextInput
            label="Frame width expr"
            value={frame.frameWidth ?? "frameWidth"}
            onChange={(value) => patchFrame({ frameWidth: value })}
          />

          <TextInput
            label="Thickness expr"
            value={frame.thickness ?? "frameThickness"}
            onChange={(value) => patchFrame({ thickness: value })}
          />
        </div>

        <TextInput
          label="Material slot"
          value={frame.materialSlot ?? "frame"}
          onChange={(value) => patchFrame({ materialSlot: value })}
          placeholder="frame"
        />
      </div>

      <div className="border rounded p-3 space-y-3 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">Infill</div>
            <div className="text-xs text-slate-500">
              Glass, mirror, board or another panel inside the frame.
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={infill.enabled !== false}
              onChange={(e) => patchInfill({ enabled: e.target.checked })}
            />
            Enabled
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Infill type</label>
          <select
            value={infill.type ?? "glass"}
            onChange={(e) => patchInfill({ type: e.target.value })}
            className="w-full px-3 py-2 border rounded bg-white"
          >
            <option value="glass">glass</option>
            <option value="mirror">mirror</option>
            <option value="board">board</option>
            <option value="metal">metal</option>
            <option value="custom">custom</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <NumberInput
            label="Infill thickness"
            value={safeParams.infillThickness ?? 4}
            onChange={(value) => patchParam("infillThickness", value)}
          />

          <NumberInput
            label="Infill inset"
            value={safeParams.infillInset ?? 12}
            onChange={(value) => patchParam("infillInset", value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <TextInput
            label="Inset expr"
            value={infill.inset ?? "frameWidth"}
            onChange={(value) => patchInfill({ inset: value })}
          />

          <TextInput
            label="Thickness expr"
            value={infill.thickness ?? "infillThickness"}
            onChange={(value) => patchInfill({ thickness: value })}
          />
        </div>

        <TextInput
          label="Material slot"
          value={infill.materialSlot ?? "infill"}
          onChange={(value) => patchInfill({ materialSlot: value })}
          placeholder="infill"
        />
      </div>
    </div>
  );
}